/**
 * UserDataSyncContext
 *
 * Syncs favorites and builds between localStorage and Cloudflare D1.
 * Strategy:
 *   - On login: version check → pull if server is newer → merge
 *   - On local change: debounce 30s → PUT to server
 *   - On tab focus: version check again
 *
 * Read/write minimization:
 *   - /api/user/data/version returns only a number (cheap)
 *   - Full pull only when version mismatch
 *   - Writes are debounced (burst of changes = 1 write)
 */

import { createContext, useContext, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { onDataChanged, emitDataChanged } from '../lib/syncEvents'

// ── localStorage keys ───────────────────────────────────────────────────────

const FAVORITE_KEYS: Record<string, string> = {
  cpu: 'cpu-favorites',
  gpu: 'gpu-favorites',
  memory: 'memory-favorites',
  storage: 'storage-favorites',
  psu: 'psu-favorites',
  case: 'case-favorites',
  motherboard: 'motherboard-favorites',
  cooler: 'cooler-favorites',
}

const COMPARISON_KEYS: Record<string, string> = {
  cpu: 'cpu-comparison',
  gpu: 'gpu-comparison',
  memory: 'memory-comparison',
  storage: 'storage-comparison',
  psu: 'psu-comparison',
  case: 'case-comparison',
  motherboard: 'motherboard-comparison',
  cooler: 'cooler-comparison',
}

const BUILD_KEY = 'pc-build-checker-components'
const SYNC_META_KEY = 'pcb-sync-meta'
const DEBOUNCE_MS = 30_000

const COMPONENTS = ['cpu', 'gpu', 'memory', 'storage', 'psu', 'case', 'motherboard', 'cooler']

/**
 * Clear all app-related localStorage keys without touching the server.
 * Used on logout so the next user starts fresh, while server data remains intact.
 */
function clearLocalAppData(): void {
  const staticKeys = [
    ...Object.values(FAVORITE_KEYS),
    BUILD_KEY,
    SYNC_META_KEY,
    ...COMPONENTS.map(c => `${c}-comparison`),
    ...COMPONENTS.map(c => `${c}-ui-mode`),
    ...COMPONENTS.map(c => `${c}-status-filter`),
    ...COMPONENTS.flatMap(c => [
      `${c}Filters_statusFilter`,
      `${c}Filters_filters`,
      `${c}Filters_searchTerm`,
      `${c}Filters_itemsPerPage`,
    ]),
    'cpu-search-history',
    'compatibility-validation-history',
  ]
  staticKeys.forEach(key => localStorage.removeItem(key))

  const prefixKeys = ['modal-search-history-', 'pc-build-cache:']
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i)
    if (key && prefixKeys.some(p => key.startsWith(p))) {
      localStorage.removeItem(key)
    }
  }
}

// ── Types ────────────────────────────────────────────────────────────────────

interface SyncMeta {
  serverVersion: number
  lastSyncAt: number
  lastCheckedAt: number
}

interface FavoritesMap {
  cpu?: string[]
  gpu?: string[]
  memory?: string[]
  storage?: string[]
  psu?: string[]
  case?: string[]
  motherboard?: string[]
  cooler?: string[]
}

type ComparisonMap = FavoritesMap

interface ServerData {
  favorites: FavoritesMap
  builds: unknown[]
  comparison: ComparisonMap
  version: number
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function readSyncMeta(): SyncMeta {
  try {
    const raw = localStorage.getItem(SYNC_META_KEY)
    if (raw) return { lastCheckedAt: 0, ...JSON.parse(raw) as SyncMeta }
  } catch { /* ignore */ }
  return { serverVersion: -1, lastSyncAt: 0, lastCheckedAt: 0 }
}

function writeSyncMeta(meta: SyncMeta): void {
  localStorage.setItem(SYNC_META_KEY, JSON.stringify(meta))
}

function readLocalFavorites(): FavoritesMap {
  const result: FavoritesMap = {}
  for (const [type, key] of Object.entries(FAVORITE_KEYS)) {
    try {
      const raw = localStorage.getItem(key)
      if (raw) (result as Record<string, string[]>)[type] = JSON.parse(raw) as string[]
    } catch { /* ignore */ }
  }
  return result
}

function writeLocalFavorites(favorites: FavoritesMap): void {
  for (const [type, key] of Object.entries(FAVORITE_KEYS)) {
    const ids = (favorites as Record<string, string[] | undefined>)[type]
    if (ids !== undefined) {
      localStorage.setItem(key, JSON.stringify(ids))
    }
  }
}

function readLocalComparison(): ComparisonMap {
  const result: ComparisonMap = {}
  for (const [type, key] of Object.entries(COMPARISON_KEYS)) {
    try {
      const raw = localStorage.getItem(key)
      if (raw) (result as Record<string, string[]>)[type] = JSON.parse(raw) as string[]
    } catch { /* ignore */ }
  }
  return result
}

function writeLocalComparison(comparison: ComparisonMap): void {
  for (const [type, key] of Object.entries(COMPARISON_KEYS)) {
    const ids = (comparison as Record<string, string[] | undefined>)[type]
    if (ids !== undefined) {
      localStorage.setItem(key, JSON.stringify(ids))
    }
  }
}

function readLocalBuilds(): unknown[] {
  try {
    const raw = localStorage.getItem(BUILD_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (Array.isArray(parsed)) return parsed
    // Legacy: single object → wrap in array for migration
    if (typeof parsed === 'object' && parsed !== null) return [parsed]
    return []
  } catch { return [] }
}

function writeLocalBuilds(builds: unknown[]): void {
  if (builds.length > 0) {
    localStorage.setItem(BUILD_KEY, JSON.stringify(builds))
  } else {
    localStorage.removeItem(BUILD_KEY)
  }
}

/** Union merge: keep all IDs from both sides (used for favorites and comparison) */
function mergeIdMap(local: FavoritesMap, server: FavoritesMap): FavoritesMap {
  const result: FavoritesMap = {}
  const allTypes = [...new Set([...Object.keys(local), ...Object.keys(server)])]
  for (const type of allTypes) {
    const localIds = (local as Record<string, string[] | undefined>)[type] ?? []
    const serverIds = (server as Record<string, string[] | undefined>)[type] ?? []
    const merged = [...new Set([...localIds, ...serverIds])]
    ;(result as Record<string, string[]>)[type] = merged
  }
  return result
}

/** Parse updatedAt: supports Unix ms (number) or ISO string */
function parseUpdatedAt(val: unknown): number {
  if (typeof val === 'number') return val
  if (typeof val === 'string') {
    const ms = new Date(val).getTime()
    return isNaN(ms) ? 0 : ms
  }
  return 0
}

/** Last-write-wins merge for builds by id + updatedAt */
function mergeBuilds(local: unknown[], server: unknown[]): unknown[] {
  const map = new Map<string, unknown>()

  const addBuild = (b: unknown) => {
    if (typeof b !== 'object' || b === null) return
    const build = b as Record<string, unknown>
    const id = typeof build['id'] === 'string' ? build['id'] : null
    if (!id) return
    const existing = map.get(id) as Record<string, unknown> | undefined
    const existingTs = existing ? parseUpdatedAt(existing['updatedAt']) : 0
    const incomingTs = parseUpdatedAt(build['updatedAt'])
    if (!existing || incomingTs >= existingTs) map.set(id, b)
  }

  server.forEach(addBuild)
  local.forEach(addBuild)
  return [...map.values()]
}

// ── Context ──────────────────────────────────────────────────────────────────

const STALE_THRESHOLD_MS = 60 * 60 * 1000 // 1 hour

interface SyncContextValue {
  /** Manually trigger a full pull from server (e.g. after login on new device) */
  forcePull: () => Promise<void>
  /** Version check if lastCheckedAt is older than thresholdMs (default: 1 hour) */
  checkIfStale: (thresholdMs?: number) => Promise<void>
  /** Delete all favorites and builds from both localStorage and server */
  clearAllData: () => Promise<void>
  /** Cancel any pending debounced push and push immediately to server */
  pushNow: () => Promise<void>
}

const SyncContext = createContext<SyncContextValue | null>(null)

export function UserDataSyncProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth()
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isSyncing = useRef(false)
  const prevStatusRef = useRef(status)

  // ── Pull from server and merge into localStorage ──────────────────────────
  const pull = useCallback(async () => {
    try {
      const res = await fetch('/api/user/data', { credentials: 'include' })
      if (!res.ok) return
      const data = await res.json() as ServerData

      const localFavorites = readLocalFavorites()
      const localBuilds = readLocalBuilds()
      const localComparison = readLocalComparison()

      writeLocalFavorites(mergeIdMap(localFavorites, data.favorites))
      writeLocalBuilds(mergeBuilds(localBuilds, data.builds))
      writeLocalComparison(mergeIdMap(localComparison, data.comparison ?? {}))
      const now = Date.now()
      writeSyncMeta({ serverVersion: data.version, lastSyncAt: now, lastCheckedAt: now })

      // Notify hooks that localStorage was updated externally
      emitDataChanged()
    } catch { /* network error — stay with local */ }
  }, [])

  // ── Push localStorage to server ───────────────────────────────────────────
  const push = useCallback(async () => {
    if (isSyncing.current) return
    isSyncing.current = true
    try {
      const body = {
        favorites: readLocalFavorites(),
        builds: readLocalBuilds(),
        comparison: readLocalComparison(),
      }
      const res = await fetch('/api/user/data', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (res.ok) {
        const { version } = await res.json() as { version: number }
        writeSyncMeta({ serverVersion: version, lastSyncAt: Date.now() })
      }
    } catch { /* network error — retry on next change */ }
    isSyncing.current = false
  }, [])

  // ── Version check: pull only if server is ahead ───────────────────────────
  const checkAndPull = useCallback(async () => {
    try {
      const res = await fetch('/api/user/data/version', { credentials: 'include' })
      if (!res.ok) return
      const { version } = await res.json() as { version: number }
      const meta = readSyncMeta()
      if (version > meta.serverVersion) {
        await pull()
      } else {
        writeSyncMeta({ ...meta, lastCheckedAt: Date.now() })
      }
    } catch { /* ignore */ }
  }, [pull])

  // ── Schedule debounced push when local data changes ───────────────────────
  const scheduleSync = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => { void push() }, DEBOUNCE_MS)
  }, [push])

  // ── On logout: cancel pending sync and clear localStorage ────────────────
  useEffect(() => {
    const wasAuthenticated = prevStatusRef.current === 'authenticated'
    prevStatusRef.current = status
    if (wasAuthenticated && status === 'unauthenticated') {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
      clearLocalAppData()
      emitDataChanged()
    }
  }, [status])

  // ── On login: version check ───────────────────────────────────────────────
  useEffect(() => {
    if (status !== 'authenticated') return
    void checkAndPull()
  }, [status, checkAndPull])

  // ── Listen for local data changes → schedule push ─────────────────────────
  useEffect(() => {
    if (status !== 'authenticated') return
    const unsub = onDataChanged(scheduleSync)
    return unsub
  }, [status, scheduleSync])

  // ── Flush pending push when tab becomes hidden (before unload) ────────────
  useEffect(() => {
    if (status !== 'authenticated') return
    const flush = () => {
      if (document.visibilityState !== 'hidden') return
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
        void push()
      }
    }
    window.addEventListener('visibilitychange', flush)
    return () => window.removeEventListener('visibilitychange', flush)
  }, [status, push])

  const forcePull = useCallback(async () => { await pull() }, [pull])

  // ── Cancel pending debounce and push immediately ──────────────────────────
  // Reuses the same pattern as the visibilitychange flush handler
  const pushNow = useCallback(async () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    await push()
  }, [push])

  const clearAllData = useCallback(async () => {
    // Cancel any pending debounced push
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }

    // Reset server data
    const res = await fetch('/api/user/data', { method: 'DELETE', credentials: 'include' })
    const newVersion = res.ok ? ((await res.json() as { version: number }).version) : 0

    // Clear all localStorage keys
    clearLocalAppData()

    // Update sync meta so no spurious re-sync occurs
    writeSyncMeta({ serverVersion: newVersion, lastSyncAt: Date.now(), lastCheckedAt: Date.now() })

    // Notify hooks to re-render with empty state
    emitDataChanged()
  }, [])

  const checkIfStale = useCallback(async (thresholdMs = STALE_THRESHOLD_MS) => {
    if (status !== 'authenticated') return
    const { lastCheckedAt } = readSyncMeta()
    if (Date.now() - lastCheckedAt >= thresholdMs) {
      await checkAndPull()
    }
  }, [status, checkAndPull])

  return (
    <SyncContext.Provider value={{ forcePull, checkIfStale, clearAllData, pushNow }}>
      {children}
    </SyncContext.Provider>
  )
}

export function useUserDataSync(): SyncContextValue {
  const ctx = useContext(SyncContext)
  if (!ctx) throw new Error('useUserDataSync must be used inside UserDataSyncProvider')
  return ctx
}
